/**
 * The flight scene's half of the ground shadows: one silhouette per aeroplane
 * near the ground, dropped onto the surface its own wheels would touch.
 *
 * Split out of `flight-scene.ts` for the reason `scene-searchlights.ts`,
 * `scene-propellers.ts` and `scene-killcam.ts` were — the host sits at aislop's
 * size ceiling and a cohesive addition belongs in a sibling. `flight-scene.ts`
 * keeps one field and one constructor call; `scene-draw.ts` makes the frame-loop
 * call. The *rules* — which sun, how far up a shadow is still worth drawing, and
 * the projection itself — are in `ground-shadow.ts`, which imports no `three`.
 *
 * ## One matrix per aeroplane per frame
 *
 * The projection is affine (`groundProjectionMatrix`), so a shadow is the
 * aeroplane's own geometry drawn through `projection · pose`. Nothing walks a
 * vertex at run time: the silhouette buffer is built once per airframe and every
 * aeroplane of that type shares it, exactly as `combat-fleet.ts` shares a model.
 * What a frame pays is one matrix compose and at most four ground queries per
 * aeroplane low enough to be drawn, plus one query for each that is not.
 *
 * ## Always the baked `.TBL` geometry, whatever ENHANCED MODELS says
 *
 * A shadow is a filled outline in one flat colour, and at the sizes one is ever
 * seen the baked LOD's outline and the enhanced `.glb`'s are the same aeroplane.
 * Taking the baked one buys three things: about 160 triangles instead of
 * thousands; no rebuild when the toggle is flipped, so there is no cache to
 * invalidate and no swap ordering to get wrong (`swapFleetModels` is the version
 * of this problem that does have to be solved); and **no propeller**, because a
 * baked `.TBL` carries none (AD-012 opens with the original drawing no propeller
 * in any flight view), which removes the whole question of a blade-shaped shadow
 * whipping round under an aeroplane whose blades are not on screen.
 *
 * ## Where it lands
 *
 * `GroundQuery.groundHeightAt` — the *same* composed answer the undercarriage
 * stops on (`fly-region.ts`: `.GI` facets over `ReliefGround.drapeAt`), never the
 * analytic relief field. That is `6471386`'s lesson applied to a second reader:
 * a shadow on the analytic surface would hover or sink over an aerodrome exactly
 * as the wheels once did, and `test/ground-shadow.test.ts` asserts the shadow and
 * the wheels agree on the same plate.
 *
 * Two queries, not one: the ground under the *aeroplane* fixes how high it is,
 * and the ground under its *shadow* is where the shadow actually lies — the same
 * point at noon, 129 m apart for an aeroplane 60 m up under a dawn sun at
 * `SHADOW_SUN_FLOOR_DEG`. Two more sample the surface a couple of metres either
 * side so the silhouette lies in the local slope rather than flat across it; the
 * drawn surface is piecewise linear over cells hundreds of metres wide, so a
 * short baseline recovers the drawn facet exactly, and an aerodrome plate — the
 * case that matters — is flat and gives a slope of zero.
 */

import * as THREE from "three/webgpu";
import type { GroundQuery, Pose, TimeOfDay } from "@red-opus/engine";
import { loadAircraftMesh } from "./aircraft-mesh.ts";
import { disposeTree } from "../three/dispose-tree.ts";
import type { EffectsSettings } from "./effects-settings.ts";
import { PLAYER_ID } from "./encounter-setup.ts";
import {
  groundProjectionMatrix,
  shadowGroundPoint,
  shadowStrengthAt,
  shadowSunFor,
  type GroundPlane,
  type SunDirection,
} from "./ground-shadow.ts";
import { SHADOW_COLOUR } from "../planes/parked-look.ts";
import { worldTrianglePositions } from "../three/silhouette-vertices.ts";
import type { EncounterRunner } from "./sim-runner.ts";

/**
 * How far the shadow plane is lifted above the surface it was sampled on,
 * metres.
 *
 * The same trick `planes/parked-aircraft.ts` uses and for the same reason: two
 * coplanar surfaces are the one arrangement a depth buffer cannot arbitrate, and
 * lessons.md records three outages caused by reaching for `depthWrite: false` to
 * settle a tie instead of removing it. Bigger than the viewer's 2 cm because
 * this surface is not a flat plate: between the sampled facet and the next the
 * drawn mesh bends, and 20 cm covers that bend at the cell sizes the relief
 * lattice uses. It is invisible from any camera this scene has — at a chase
 * camera's twenty metres it is under a fifth of a degree.
 */
export const SHADOW_LIFT_M = 0.2;

/**
 * Baseline for the two extra ground samples that give the shadow plane its
 * slope, metres.
 *
 * Short on purpose. The drawn surface is piecewise linear, so two metres lands
 * inside the same facet as the centre sample almost everywhere and recovers that
 * facet's own gradient exactly rather than an average across several. It also
 * keeps the sample inside an aerodrome plate: a longer baseline would step off
 * the edge of one, read the terrain below it, and tilt the shadow of a parked
 * aeroplane by the whole height of the plate.
 */
export const SLOPE_BASELINE_M = 2;

/**
 * Steepest slope the shadow plane will take, as a gradient (tan of the angle).
 *
 * A guard, not a look: 1 in 2 is steeper than anything the relief produces, so
 * it never engages on open ground. What it bounds is the one place a two-metre
 * baseline can still straddle a step — the rim of a placed plate — where an
 * unclamped fit would stand the shadow on its edge for the one frame an
 * aeroplane taxis across it.
 */
export const MAX_SHADOW_GRADIENT = 0.5;

/** One aeroplane worth drawing a shadow for: which model, and where it is this frame. */
export interface ShadowSource {
  readonly id: string;
  /** `null` where the airframe has no baked geometry — such an aeroplane draws none. */
  readonly modelId: string | null;
  readonly pose: Pose;
}

export interface GroundShadows {
  /** Add this to the scene once; every shadow draws inside it. */
  readonly group: THREE.Group;
  /**
   * The sun this sortie's shadows are cast by, settled once at construction from
   * the briefed hour — `undefined` at night, which is the whole of the night
   * case and costs one comparison a frame.
   */
  readonly sun: SunDirection | undefined;
  /** Places this frame's shadows. `enabled` false hides every one of them. */
  update(sources: readonly ShadowSource[], enabled: boolean): void;
  /** Shadows placed by the last `update` — what the tests and the draw count read. */
  readonly liveCount: number;
  /** Draw calls the shadows add this frame: one apiece, and none while nothing is low enough. */
  drawCallCount(): number;
  dispose(): void;
}

/** One participant's shadow: its own material, because the strength is per aeroplane. */
interface ShadowSlot {
  readonly material: THREE.MeshBasicNodeMaterial;
  /** `null` until the airframe's silhouette has been fetched and built. */
  mesh: THREE.Mesh | null;
}

/**
 * The silhouette buffer for one airframe: its baked triangles, in the frame
 * `placeAircraft` poses a model in, positions and nothing else.
 */
async function silhouetteGeometry(modelId: string): Promise<THREE.BufferGeometry | undefined> {
  // Baked, never enhanced — see the module doc. The loaded object is a scaffold:
  // its vertices are copied out and it is freed, so nothing here holds a second
  // copy of an aeroplane the fleet is already drawing.
  const object = await loadAircraftMesh(modelId);
  if (!object) return undefined;
  const positions = worldTrianglePositions(object);
  disposeTree(object);
  if (positions.length < 9) return undefined;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  return geometry;
}

/**
 * The plane the shadow lies in: the drawn surface under the shadow's own centre,
 * tilted to that surface's local slope and lifted clear of it.
 */
export function shadowPlaneAt(ground: GroundQuery, x: number, z: number): GroundPlane {
  const height = ground.groundHeightAt(x, z);
  const alongX = (ground.groundHeightAt(x + SLOPE_BASELINE_M, z) - height) / SLOPE_BASELINE_M;
  const alongZ = (ground.groundHeightAt(x, z + SLOPE_BASELINE_M) - height) / SLOPE_BASELINE_M;
  const clamp = (gradient: number): number => Math.max(-MAX_SHADOW_GRADIENT, Math.min(MAX_SHADOW_GRADIENT, gradient));
  // The upward normal of `y = height + a·dx + b·dz` is `(-a, 1, -b)`; the lift is
  // applied vertically rather than along it, which is the same thing to within
  // the cosine of a slope this shallow.
  return { x, y: height + SHADOW_LIFT_M, z, normalX: -clamp(alongX), normalY: 1, normalZ: -clamp(alongZ) };
}

class GroundShadowsImpl implements GroundShadows {
  readonly group = new THREE.Group();
  readonly sun: SunDirection | undefined;
  private readonly groundRef: { readonly current: GroundQuery };
  private readonly slots = new Map<string, ShadowSlot>();
  /** `modelId` → its silhouette, fetched once however many aeroplanes of that type are flying. */
  private readonly geometries = new Map<string, Promise<THREE.BufferGeometry | undefined>>();
  private placed = 0;
  private disposed = false;
  /** Scratch, reused every frame: a shadow is one matrix and this is where it is built. */
  private readonly projection = new THREE.Matrix4();
  private readonly poseMatrix = new THREE.Matrix4();
  private readonly posePosition = new THREE.Vector3();
  private readonly poseOrientation = new THREE.Quaternion();
  private readonly poseScale = new THREE.Vector3(1, 1, 1);

  constructor(timeOfDay: TimeOfDay | undefined, groundRef: { readonly current: GroundQuery }) {
    this.sun = shadowSunFor(timeOfDay);
    this.groundRef = groundRef;
  }

  private slotFor(id: string, modelId: string): ShadowSlot {
    const existing = this.slots.get(id);
    if (existing) return existing;
    const material = new THREE.MeshBasicNodeMaterial({ color: SHADOW_COLOUR, side: THREE.DoubleSide });
    material.transparent = true;
    // Depth-tested but not depth-writing, the same arrangement AD-012's discs
    // take: the shadow is a see-through mark lying on the ground, and writing
    // depth would punch an aeroplane-shaped hole in everything drawn after it.
    material.depthWrite = false;
    const slot: ShadowSlot = { material, mesh: null };
    this.slots.set(id, slot);
    void this.geometryFor(modelId).then((geometry) => {
      if (!geometry || this.disposed) return;
      const mesh = new THREE.Mesh(geometry, material);
      mesh.matrixAutoUpdate = false;
      // The projection is rank-3 — it flattens space onto the ground — so the
      // bounding sphere three.js would derive from this matrix is not one to cull
      // against. There are at most a dozen shadows and each is one draw call.
      mesh.frustumCulled = false;
      // Ahead of every other transparent thing in the scene, because it lies
      // behind all of them: smoke, fire and tracer hang in the air above the
      // ground this is painted on.
      mesh.renderOrder = -1;
      mesh.visible = false;
      slot.mesh = mesh;
      this.group.add(mesh);
    });
    return slot;
  }

  private geometryFor(modelId: string): Promise<THREE.BufferGeometry | undefined> {
    const existing = this.geometries.get(modelId);
    if (existing) return existing;
    const pending = silhouetteGeometry(modelId).catch(() => undefined);
    this.geometries.set(modelId, pending);
    return pending;
  }

  update(sources: readonly ShadowSource[], enabled: boolean): void {
    this.placed = 0;
    const ground = this.groundRef.current;
    const sun = this.sun;
    if (!enabled || !sun) {
      for (const slot of this.slots.values()) if (slot.mesh) slot.mesh.visible = false;
      return;
    }
    const live = new Set<string>();
    for (const source of sources) {
      if (source.modelId === null) continue;
      live.add(source.id);
      this.place(this.slotFor(source.id, source.modelId), source.pose, ground, sun);
    }
    for (const [id, slot] of this.slots) {
      if (!live.has(id) && slot.mesh) slot.mesh.visible = false;
    }
  }

  /** One aeroplane: decide whether it is low enough, then build its matrix. */
  private place(slot: ShadowSlot, pose: Pose, ground: GroundQuery, sun: SunDirection): void {
    const { x, y, z } = pose.position;
    // Height above the surface the *aeroplane* is over. Measured from the pose
    // origin rather than the wheels, which is a metre or two of an altitude band
    // 240 m deep and not worth a second query for the stance.
    const underAircraft = ground.groundHeightAt(x, z);
    const strength = shadowStrengthAt(y - underAircraft);
    if (strength <= 0) {
      if (slot.mesh) slot.mesh.visible = false;
      return;
    }
    const mesh = slot.mesh;
    if (!mesh) return;
    const under = shadowGroundPoint(x, y, z, underAircraft, sun);
    const elements = groundProjectionMatrix(sun, shadowPlaneAt(ground, under.x, under.z));
    if (!elements) {
      mesh.visible = false;
      return;
    }
    this.posePosition.set(x, y, z);
    this.poseOrientation.set(pose.orientation.x, pose.orientation.y, pose.orientation.z, pose.orientation.w);
    this.poseMatrix.compose(this.posePosition, this.poseOrientation, this.poseScale);
    mesh.matrix.copy(this.projection.fromArray(elements).multiply(this.poseMatrix));
    mesh.matrixWorldNeedsUpdate = true;
    slot.material.opacity = strength;
    mesh.visible = true;
    this.placed += 1;
  }

  get liveCount(): number {
    return this.placed;
  }

  drawCallCount(): number {
    return this.placed;
  }

  dispose(): void {
    this.disposed = true;
    for (const slot of this.slots.values()) slot.material.dispose();
    for (const pending of this.geometries.values()) void pending.then((geometry) => geometry?.dispose());
    this.slots.clear();
    this.geometries.clear();
  }
}

/**
 * Builds the shadow pool and adds it to `scene`.
 *
 * Always built, whatever the hour — an empty group costs nothing, and the night
 * gate is on what is *fed* to it (`GroundShadows.sun`) rather than on whether it
 * exists, the same shape `createSearchlights` takes for the opposite hour.
 */
export function createGroundShadows(
  scene: THREE.Scene,
  timeOfDay: TimeOfDay | undefined,
  /**
   * The region's ground, through the same swappable ref the sim reads. A ref
   * rather than a value because `fly-region.ts` fills it after the scene is
   * built, and a shadow taken on the flat default before then is on the surface
   * the aeroplane is flat on too.
   */
  groundRef: { readonly current: GroundQuery },
): GroundShadows {
  const shadows = new GroundShadowsImpl(timeOfDay, groundRef);
  scene.add(shadows.group);
  return shadows;
}

/** What `flight-scene.ts`'s own state has to offer for the shadows to be wired up. */
export interface ShadowHost {
  readonly runner: EncounterRunner;
  readonly playerModelId: string;
  readonly effects: EffectsSettings;
  readonly shadows: GroundShadows;
}

/**
 * Every aeroplane in the sky, player first — the same list AD-012's discs are
 * collected from, minus everything a propeller needs.
 *
 * Balloons, Zeppelins and the raid targets are deliberately absent: they are
 * moored or parked objects whose own shadow, if anybody wants one, is a property
 * of the scenery rather than of the fleet.
 */
export function shadowSourcesFor(host: ShadowHost, pose: Pose): ShadowSource[] {
  const sources: ShadowSource[] = [{ id: PLAYER_ID, modelId: host.playerModelId, pose }];
  for (const other of host.runner.others()) sources.push({ id: other.id, modelId: other.modelId, pose: other.pose });
  return sources;
}

/**
 * Whether shadows are drawn this frame — **ENHANCED EFFECTS, and nothing else**.
 *
 * The original draws no shadow under an aeroplane in flight (`ground-shadow.ts`
 * has the pixel counts that settled it), so this is a port extension and belongs
 * under an existing toggle rather than a new row. ENHANCED EFFECTS is the one
 * that already means "visual phenomena around an aeroplane the original did not
 * draw", it is on by default, and with it off a sortie's pixels are exactly what
 * they were before this feature existed.
 */
export function groundShadowsDrawn(host: ShadowHost): boolean {
  return host.effects.enhancedEffects;
}

/** Places this frame's shadows. One call, so `scene-draw.ts` keeps one line. */
export function updateGroundShadows(host: ShadowHost, pose: Pose): void {
  host.shadows.update(shadowSourcesFor(host, pose), groundShadowsDrawn(host));
}
